import { Flex, Box, Text } from "rebass/styled-components";
import CustomDayPicker from "../datepicker/CustomDayPicker";
import styled from "styled-components";
import gql from "graphql-tag";
import { lighten } from "polished";
import Heading from "../common/Heading";
import Trans from "next-translate/Trans";
import { useState, useContext } from "react";
import { addYears, format, formatDistanceToNow, parseISO } from "date-fns";
import ServiceSelect from "../common/ServiceSelect";
import { useQuery } from "@apollo/client";
import { Caregiver, CurrencyIso } from "../../interfaces";
import { LocaleContext } from "../locale";
import { getDateFnsLocale } from "../../lib/utils";

const availabilityQuery = gql`
  query getAvailability($caregiverId: ID!, $serviceTypeId: ID!) {
    availability(caregiverId: $caregiverId, serviceTypeId: $serviceTypeId) {
      unavailable
      booked
    }
  }
`;

const AvailabilityCalendar: typeof CustomDayPicker = styled(CustomDayPicker)`
  overflow: hidden;

  .DayPicker-wrapper {
    padding: ${({ theme }) => theme.space[4]}px 0;

    .DayPicker-NavBar {
      left: 0;
      right: 0;
    }

    .DayPicker-Month {
      width: 100%;
    }

    .DayPicker-Week,
    .DayPicker-WeekdaysRow {
      display: flex;
      align-items: baseline;
    }

    .DayPicker-Day,
    .DayPicker-Weekday {
      width: initial !important;
      flex-basis: 100%;
    }

    .DayPicker-Day {
      height: 100%;
      position: relative;
      background-color: ${({ theme }) =>
        lighten(0.4, theme.colors.springGreen)} !important;
      border-radius: 0;
      color: ${({ theme }) => theme.colors.springGreen};
      :hover {
        cursor: default;
      }
    }

    .DayPicker-Day--booked {
      background-color: ${({ theme }) =>
        lighten(0.4, theme.colors.errorRed)} !important;
      color: ${({ theme }) => theme.colors.errorRed} !important;
    }

    .DayPicker-Day--unavailable:not(.DayPicker-Day--booked) {
      background-color: ${({ theme }) => theme.colors.samoyedWhite} !important;
      color: ${({ theme }) => theme.colors.labradorBlack} !important;
      overflow: hidden;
      :before {
        position: absolute;
        top: -50%;
        bottom: -50%;
        left: 50%;
        width: 1px;
        transform: rotateZ(45deg);
        background-color: red;
        content: "";
      }
    }
  }
`;

const CustomDay = day => (
  <Box flexBasis="100%" width="100%" pt="100%">
    <Box
      as="span"
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate3d(-50%, -50%, 0)"
      }}
    >
      {day.getDate()}
    </Box>
  </Box>
);

//used solely for maintainig a logical heading organisation
const CustomCaption = ({ date }: any) => {
  const { locale } = useContext(LocaleContext);
  const calendarLocale = getDateFnsLocale(locale);

  return (
    <Box className="DayPicker-Caption">
      <Heading
        as="h3"
        textAlign="center"
        lineHeight="48px"
        fontSize={5}
        fontWeight="book"
      >
        {format(date, "MMMM yyyy", { locale: calendarLocale })}
      </Heading>
    </Box>
  );
};

interface Props {
  services: {
    serviceTypeId: number;
    price: number;
    pickUpDropOff: boolean;
    pickUpDropOffPrice: number;
    pickUpDropOffDistance: number;
    holidayPrice: number;
    puppyPrice: number;
    additionalDogPrice: number;
    currency: CurrencyIso;
  }[];
  caregiver: Caregiver;
  width?: any;
  numberOfMonths?: number;
  toMonth?: Date;
}

const Availability = ({
  services,
  width,
  numberOfMonths,
  toMonth,
  caregiver
}: Props) => {
  const [serviceTypeId, setServiceTypeId] = useState(services[0].serviceTypeId);
  const { locale } = useContext(LocaleContext);
  const formattingLocale = getDateFnsLocale(locale);
  const caregiverId = caregiver.id;

  const { loading, data } = useQuery(availabilityQuery, {
    variables: {
      serviceTypeId,
      caregiverId
    }
  });

  const serviceAvailability =
    loading || !data
      ? {}
      : {
          booked: data.availability.booked.map(date => parseISO(date)),
          unavailable: data.availability.unavailable.map(date => parseISO(date))
        };

  return (
    <Flex flexWrap="wrap">
      <ServiceSelect
        initial={services[0].serviceTypeId}
        services={services.map(({ serviceTypeId }) => serviceTypeId)}
        onClickHandler={setServiceTypeId}
      />
      <Box
        sx={{
          width: width
        }}
      >
        <AvailabilityCalendar
          captionElement={CustomCaption}
          numberOfMonths={numberOfMonths}
          toMonth={toMonth}
          renderDay={CustomDay}
          modifiers={serviceAvailability}
          fromMonth={new Date()}
        />
      </Box>
      <Box width={1}>
        <Flex flexWrap="wrap" alignContent="flex-start" alignItems="baseline">
          <Box>
            <Text
              mr={2}
              mb={1}
              sx={{
                position: "relative",
                paddingLeft: 4,
                ":before": {
                  position: "absolute",
                  top: "-1px",
                  left: 0,
                  width: "24px",
                  height: "24px",
                  backgroundColor: lighten(0.4, "#3CB5A1"),
                  content: `" "`,
                  borderRadius: "circle"
                }
              }}
            >
              <Trans i18nKey="sitterprofile:available" />
            </Text>
          </Box>
          <Box>
            <Text
              mr={2}
              mb={1}
              sx={{
                position: "relative",
                paddingLeft: 4,
                ":before": {
                  position: "absolute",
                  top: "-1px",
                  left: 0,
                  width: "24px",
                  height: "24px",
                  backgroundColor: lighten(0.4, "#B31412"),
                  content: `" "`,
                  borderRadius: "circle"
                }
              }}
            >
              <Trans i18nKey="sitterprofile:reserved" />
            </Text>
          </Box>
          <Box>
            <Text
              mb={1}
              sx={{
                position: "relative",
                paddingLeft: 4,
                "::after": {
                  position: "absolute",
                  top: "0",
                  left: "12px",
                  width: "1px",
                  height: "24px",
                  transform: "rotateZ(45deg)",
                  backgroundColor: "red",
                  content: `" "`
                },
                "::before": {
                  position: "absolute",
                  top: "-1px",
                  left: 0,
                  width: "23px",
                  height: "23px",
                  backgroundColor: "samoyedWhite",
                  content: `" "`,
                  borderRadius: "circle",
                  border: "1px solid",
                  borderColor: "labradorBlack"
                }
              }}
            >
              <Trans i18nKey="sitterprofile:unavailable" />
            </Text>
          </Box>
        </Flex>
      </Box>
      {caregiver.caregiverCalendarUpdatedAt && (
        <Box pt={2}>
          <Text
            sx={{
              fontSize: 2,
              fontWeight: "medium"
            }}
          >
            <Trans
              i18nKey="sitterprofile:calendarAmended"
              values={{
                lastUpdated: formatDistanceToNow(
                  new Date(caregiver.caregiverCalendarUpdatedAt),
                  {
                    locale: formattingLocale
                  }
                )
              }}
            />
          </Text>
        </Box>
      )}
    </Flex>
  );
};

Availability.defaultProps = {
  numberOfMonths: 1,
  toMonth: addYears(new Date(), 1),
  width: "100%"
};

export default Availability;
